import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { AXIOS_API_INSTANCE } from "../../../Constants"
import { send_authorized_request } from "../../../Constants/cognito"
import { useSnackbar } from 'notistack';
import { Box, Checkbox, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, MenuItem, Select, Tooltip } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { LoadingBackdropIndeterminate } from "../../LoadingBackdrop"
import { parseMoneyInput } from "../../../Utils"

const fee_types = [
    'Flat Rate'
]

export default function ServiceOrderFeeModal({
    serviceorderfee,
    open,
    closeModal,
    creatingNewServiceOrderFee,
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [myServiceOrderFee, setMyServiceOrderFee] = React.useState(serviceorderfee)
    const [saving, setSaving] = React.useState(false)
    React.useEffect(() => {
        setMyServiceOrderFee(serviceorderfee)
    }, [serviceorderfee])

    const handleChange = (prop) => (event) => {
        if (prop === "part_price_adjustment") {
            if ((event.nativeEvent.data === '-' && (parseInt(myServiceOrderFee[prop]) || 0) === 0) || (event.target.value === '' && parseInt(myServiceOrderFee[prop]) < 0)) {
                setMyServiceOrderFee({ ...myServiceOrderFee, [prop]: '-0' })
            } else {
                setMyServiceOrderFee({ ...myServiceOrderFee, [prop]: Math.max(-100, parseInt(event.target.value)) || 0 })
            }
        } else if (["default_minutes_worked"].includes(prop)) {
            setMyServiceOrderFee({ ...myServiceOrderFee, [prop]: parseInt(event.target.value) || 0 })
        } else if (["default_flat_rate", "hourly_rate"].includes(prop)) {
            setMyServiceOrderFee({ ...myServiceOrderFee, [prop]: parseMoneyInput(myServiceOrderFee[prop], event.target.value, { allow_empty: true }) || 0 })
        } else if (["has_active_times", "recurring_active_time", "taxable"].includes(prop)) {
            setMyServiceOrderFee({ ...myServiceOrderFee, [prop]: event.target.checked })
        } else if (["start_active_time", "end_active_time"].includes(prop)) {
            setMyServiceOrderFee({ ...myServiceOrderFee, [prop]: event })
        } else {
            setMyServiceOrderFee({ ...myServiceOrderFee, [prop]: event.target.value })
        }
    }

    const handle_calculation_data_change = (prop) => (e) => {
        if (['editable'].includes(prop)) {
            setMyServiceOrderFee({ ...myServiceOrderFee, calculation_data: { ...myServiceOrderFee.calculation_data, [prop]: e.target.checked } })
        }
        if (['flat_rate'].includes(prop)) {
            const value = parseMoneyInput(myServiceOrderFee.calculation_data[prop], e.target.value, { allow_negative: true, allow_empty: true }) || 0
            setMyServiceOrderFee({ ...myServiceOrderFee, calculation_data: { ...myServiceOrderFee.calculation_data, [prop]: value } })
        }
    }

    const save = (event) => {
        setSaving(true)
        delete myServiceOrderFee.date_created
        delete myServiceOrderFee.date_updated
        if (creatingNewServiceOrderFee) {
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.put(`serviceorderfees`, myServiceOrderFee).then(() => {
                    setSaving(false)
                    enqueueSnackbar('Successfully created service order fee', { variant: 'success' })
                    closeModal(true)
                }).catch((error) => {
                    setSaving(false)
                    enqueueSnackbar(`Failed to create service order fee <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        } else {
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.post(`serviceorderfees/${myServiceOrderFee.id}`, myServiceOrderFee).then(() => {
                    enqueueSnackbar('Successfully updated service order fee', { variant: 'success' })
                    setSaving(false)
                    closeModal(true)
                }).catch((error) => {
                    setSaving(false)
                    enqueueSnackbar(`Failed to update service order fee <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        }
    }
    const deleteServiceOrderFee = () => {
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.delete(`serviceorderfees/${myServiceOrderFee.id}`).then(
                () => {
                    enqueueSnackbar('Successfully deleted service order fee', { variant: 'success' })
                    closeModal(true)
                })
        )
    }

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                maxWidth={'lg'}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <LoadingBackdropIndeterminate
                    open={saving}
                    text={"Saving Service Order Fee"}
                />
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>{creatingNewServiceOrderFee ? "Creating Service Order Fee" : "Modifying Service Order Fee"}</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ width: '100%' }}>
                        <Grid container spacing={2}>
                            <Grid size={7} container direction="column" spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="name"
                                        label="Name"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myServiceOrderFee?.name ?? ''}
                                        onChange={handleChange("name")}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="description"
                                        label="Description"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myServiceOrderFee?.description ?? ''}
                                        onChange={handleChange("description")}
                                        multiline
                                        minRows={2}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="sku"
                                        label="SKU"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myServiceOrderFee?.sku ?? ''}
                                        onChange={handleChange("sku")}
                                    />
                                </Grid>
                                <Grid size="auto">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={myServiceOrderFee?.taxable || false}
                                                onChange={handleChange("taxable")}
                                            />
                                        }
                                        label="Taxable"
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <FormControl fullWidth required>
                                        <InputLabel id="service-order-fee-select-fee-type">Fee Type</InputLabel>
                                        <Select
                                            labelId="service-order-fee-select-fee-type"
                                            label="Fee Type"
                                            placeholder='Select Fee Type...'
                                            required
                                            onChange={handleChange("fee_type")}
                                            value={myServiceOrderFee.fee_type ?? ''}
                                            sx={{ width: '100%' }}
                                        >
                                            {fee_types.map((type) =>
                                                <MenuItem
                                                    key={type}
                                                    value={type}
                                                >
                                                    {type}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            {myServiceOrderFee.fee_type === 'Flat Rate' &&
                                <Grid size={5} container direction="column" spacing={1} justifyContent={'flex-start'}>
                                    <Grid>
                                        <TextField
                                            margin="dense"
                                            id="flat_rate_calculation"
                                            label="Flat Rate"
                                            type="number"
                                            inputMode="numeric"
                                            fullWidth
                                            variant="filled"
                                            value={(myServiceOrderFee?.calculation_data?.flat_rate ?? '0').toString()}
                                            onChange={handle_calculation_data_change("flat_rate")}
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            {<AttachMoneyIcon />}
                                                        </InputAdornment>
                                                    )
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={myServiceOrderFee?.calculation_data?.editable ?? false}
                                                    onChange={handle_calculation_data_change("editable")}
                                                />
                                            }
                                            label="Editable on Quote/Invoice"
                                        />
                                    </Grid>
                                </Grid>
                            }
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid size="auto">
                            {!creatingNewServiceOrderFee &&
                                <Tooltip title={myServiceOrderFee?.deletable_reason}>
                                    <span> {/* span required for tooltip events */}
                                        <Button disabled={!myServiceOrderFee?.deletable} variant='outlined' color='error' onClick={deleteServiceOrderFee} sx={{ mr: 1 }}>
                                            <DeleteIcon /> <Box sx={{ pl: 1 }}><span>Delete</span></Box>
                                        </Button>
                                    </span>
                                </Tooltip>
                            }
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" autoFocus onClick={save}>
                                {creatingNewServiceOrderFee ? "Create" : "Save"}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
        </div>)
    );
}
